import CloseIcon from '@mui/icons-material/Close';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import SettingsBrightnessOutlinedIconIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import type { DrawerProps } from '@mui/material';
import { Divider, Drawer, Stack, styled, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useColorScheme } from '@mui/material/styles';
import type { MouseEvent } from 'react';

const COLOR_MODES = [
  {
    value: 'light',
    icon: <LightModeOutlinedIcon />,
  },
  {
    value: 'system',
    icon: <SettingsBrightnessOutlinedIconIcon />,
  },
  {
    value: 'dark',
    icon: <DarkModeOutlinedIcon />,
  },
];

const StyledToggleButton = styled(ToggleButton)({
  textTransform: 'capitalize',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

type UserPreferencesDrawerProps = {
  setOpen: (open: boolean) => void;
} & DrawerProps;

export function UserPreferencesDrawer({ children, open, setOpen }: UserPreferencesDrawerProps) {
  const { mode, setMode } = useColorScheme();

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const handleModeToggle = (_e: MouseEvent<HTMLElement>, toggleValue: typeof mode) => {
    if (toggleValue) {
      setMode(toggleValue);
    }
  };

  return (
    <Drawer open={open} onClose={toggleDrawer(false)} anchor="right">
      <Box>
        <Stack
          sx={{ justifyContent: 'space-between', flexDirection: 'row', alignItems: 'center', py: 2, px: 4, gap: 2 }}
        >
          <SettingsOutlinedIcon />
          <Typography sx={{ justifySelf: 'left' }}>Settings</Typography>
          <IconButton onClick={toggleDrawer(false)} aria-label="Close" sx={{ marginRight: -3 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Divider />
        <Box>
          <Stack sx={{ p: 4, gap: 2 }}>
            <Typography variant="body2">Mode</Typography>
            <ToggleButtonGroup exclusive value={mode} aria-label="color scheme mode" onChange={handleModeToggle}>
              {COLOR_MODES.map(({ value, icon }) => (
                <StyledToggleButton value={value} key={value}>
                  {icon}
                  {value}
                </StyledToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
          {children}
        </Box>
      </Box>
    </Drawer>
  );
}
